// [cursor 2026-06-01] S87-T4 finish-up: durable bookmark save path + change bus.
// Components call saveBookmark() directly; the legacy `fintheon:bookmark-save` event
// is also routed here by a single app-level sink (useBookmarkSink). The backend
// upserts on (user_id, source_type, source_id), and an in-flight guard prevents
// duplicate saves for the same source item within a tick.
import backendClient from "./backend";
import type { BookmarkInput, BookmarkRecord } from "./services/bookmarks";

export const BOOKMARK_SAVE_EVENT = "fintheon:bookmark-save";
export const BOOKMARKS_CHANGED_EVENT = "fintheon:bookmarks-changed";

export type BookmarkSaveDetail = BookmarkInput;

function sanitizeUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? url
      : null;
  } catch {
    return null;
  }
}

export function bookmarkKey(sourceType: string, sourceId: string): string {
  return `${sourceType}:${sourceId}`;
}

const inFlight = new Set<string>();

export async function saveBookmark(
  input: BookmarkSaveDetail,
): Promise<BookmarkRecord | null> {
  const key = bookmarkKey(input.sourceType, input.sourceId);
  if (inFlight.has(key)) return null;
  inFlight.add(key);
  try {
    const record = await backendClient.bookmarks.save({
      ...input,
      url: sanitizeUrl(input.url),
      imageUrl: sanitizeUrl(input.imageUrl),
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(BOOKMARKS_CHANGED_EVENT, { detail: { record } }),
      );
    }
    return record;
  } finally {
    inFlight.delete(key);
  }
}

export function dispatchBookmarkSave(input: BookmarkSaveDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BOOKMARK_SAVE_EVENT, { detail: input }));
}
